import type { RegionKey, USState } from '../types';
import { ACCOMMODATIONS, CHEESERIES } from './data';
import { REGIONS } from './regions';

/**
 * @deprecated — alias kept for backward compatibility with existing callers
 * in `App.tsx`. Prefer `RegionKey` from `../types`.
 */
export type RegionHubKey = RegionKey;

export interface TrailStateHub {
  state: USState;
  /**
   * URL slug for the state's dedicated chapter page. Empty string means
   * the state has curated data but no standalone hub page yet — UI
   * elements that link to the chapter should be hidden in that case.
   */
  slug: string;
  title: string;
  eyebrow: string;
  subtitle: string;
  bestFor: string[];
  accent: string;
}

/**
 * View model for a region's card on the Trails tab. `cheeseIds` and
 * `stayIds` are derived at module load from the `region` field on each
 * record, so the only source of truth for membership is the data itself.
 */
export interface TrailRegionHub {
  key: RegionKey;
  state: USState;
  title: string;
  shortTitle: string;
  subtitle: string;
  mood: string;
  cheeseIds: string[];
  stayIds: string[];
}

export const STATE_HUBS: TrailStateHub[] = [
  {
    state: 'VT',
    slug: 'vermont-cheese-trail',
    title: 'Vermont Cheese Trail',
    eyebrow: 'Pasture roads / alpine cellars',
    subtitle:
      'Raw-milk cheddar, hillside aging caves, maple-country farm stores, and Route 100 weekends through the Green Mountains.',
    bestFor: ['Historic cheddar', 'Farm stores', 'Mountain inns', 'Maple pairings'],
    accent: 'Moss, cream, barn red',
  },
  {
    state: 'ME',
    slug: 'maine-cheese-trail',
    title: 'Maine Cheese Trail',
    eyebrow: 'Coastal inns / working farms',
    subtitle:
      'Goat dairies, midcoast markets, Portland-area day trips, and creamery stops that pair naturally with harbors and Acadia.',
    bestFor: ['Coastal weekends', 'Goat cheese', 'Farm markets', 'Acadia detours'],
    accent: 'Sea blue, weathered wood, butter yellow',
  },
  {
    state: 'NH',
    slug: 'new-hampshire-cheese-trail',
    title: 'New Hampshire Cheese Trail',
    eyebrow: 'Notch roads / country stores',
    subtitle:
      'Self-serve farmstands, a historic country store, and biodynamic dairy stops — a quieter chapter between the Connecticut River and the Lakes Region.',
    bestFor: ['Historic country stores', 'Self-serve farmstands', 'Biodynamic dairies', 'White Mountains weekends'],
    accent: 'Granite, birch, moss',
  },
];

/** Returns the chapter URL for a state, or `null` when no page exists. */
export function getStateHubSlug(state: USState): string | null {
  const hub = STATE_HUBS.find((h) => h.state === state);
  return hub && hub.slug ? `/${hub.slug}` : null;
}

/**
 * Region hubs are derived from the `REGIONS` registry + the `region` field
 * on each cheesery / accommodation. This keeps membership authoritative in
 * one place (the data) and makes adding a new stop a one-line change.
 */
export const REGION_HUBS: TrailRegionHub[] = REGIONS.map((r) => ({
  key: r.key,
  state: r.state,
  title: r.title,
  shortTitle: r.shortTitle,
  subtitle: r.subtitle,
  mood: r.mood,
  cheeseIds: CHEESERIES.filter((c) => c.region === r.key).map((c) => c.id),
  stayIds: ACCOMMODATIONS.filter((a) => a.region === r.key).map((a) => a.id),
}));

export function getStateHub(state: USState): TrailStateHub | undefined {
  return STATE_HUBS.find((hub) => hub.state === state);
}

export function getRegionHubForLocation(locationId: string): TrailRegionHub | undefined {
  return REGION_HUBS.find(
    (hub) => hub.cheeseIds.includes(locationId) || hub.stayIds.includes(locationId)
  );
}
